import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { StripeService } from '../services/stripeService';
import { AppColors } from '../theme/colors';

/**
 * 無料実証から有料利用へ切り替える案内。会社管理に置く。
 *
 * 会社はそのまま使うため、投稿・テーマ・QRコード・登録端末は引き継がれる。
 * 課金開始は無料期間の終了日の翌日（= 翌月1日）で、切り替えを早めても
 * その分を損しない。
 *
 * お支払いには郵便番号と住所が必要になる。無料のご登録時には受け取って
 * いないため、未入力なら基本情報からの登録をご案内し、手続きは止める。
 * 同じ項目をこのカードでも入力できるようにすると、画面に二重に出てしまう。
 */

/**
 * いま提供しているプランは1つだけ（PlanSelectionPage でも他はコメントアウト）。
 * 増えたら選べるようにする。
 */
const PLAN_NAME = 'プラン50';
const PLAN_PRICE_LABEL = '月額 12,000円（税込 13,200円）';

interface FreeTrialUpgradeCardProps {
  companyId: string;
  /** 無料期間の終了日。課金開始日の案内に使う */
  freeTrialEndDate?: { toDate?: () => Date } | Date | null;
  /** お支払いに必要。未入力なら基本情報からの登録をご案内する */
  postalCode?: string;
  address?: string;
  /** 期間が終了して停止しているか。文言を変える */
  isSuspended?: boolean;
}

export const FreeTrialUpgradeCard: React.FC<FreeTrialUpgradeCardProps> = ({
  companyId,
  freeTrialEndDate,
  postalCode,
  address,
  isSuspended = false,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'bank_transfer'>('card');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const billingStartLabel = formatBillingStart(freeTrialEndDate);
  // お支払いには住所が要る。Stripe の顧客情報に渡すため
  const hasAddress = Boolean(postalCode?.trim()) && Boolean(address?.trim());

  const handleSubmit = async () => {
    setError(null);

    if (!hasAddress) {
      setError('先に基本情報から郵便番号と住所をご登録ください');
      return;
    }

    const { priceId } = StripeService.resolvePriceId(PLAN_NAME);
    if (!priceId) {
      setError('プランの情報を取得できませんでした。時間をおいて再度お試しください。');
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        companyId,
        planName: PLAN_NAME,
        priceId,
        paymentMethod,
        postalCode: postalCode!.trim(),
        address: address!.trim(),
      };

      if (paymentMethod === 'card') {
        const call = httpsCallable<typeof params, { url: string }>(
          functions,
          'createUpgradeCheckoutSession'
        );
        const { data } = await call(params);
        // Stripe のお支払い画面へ移る
        window.location.href = data.url;
        return;
      }

      const call = httpsCallable<typeof params, { success: boolean }>(
        functions,
        'createUpgradeSubscriptionWithBankTransfer'
      );
      await call(params);
      // 画面全体を読み込み直して、有料の表示に切り替える
      window.location.reload();
    } catch (err) {
      console.error('[有料への切り替え] エラー', err);
      const isJapanese = err instanceof Error && /[ぁ-んァ-ヶ一-龠]/.test(err.message);
      setError(
        isJapanese
          ? (err as Error).message
          : 'お手続きを開始できませんでした。時間をおいて再度お試しください。'
      );
      setIsLoading(false);
    }
  };

  return (
    <Card
      id="free-trial-upgrade"
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: `1px solid ${AppColors.primary}`,
        backgroundColor: AppColors.primaryLighter,
        mb: 3,
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black', mb: 1 }}>
        有料利用へ切り替える
      </Typography>

      <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 2, lineHeight: 1.7 }}>
        {isSuspended ? (
          <>
            お支払い情報をご登録いただくと、すぐにご利用を再開いただけます。<br />
            これまでの投稿・テーマ・QRコード・ご登録の端末は、そのまま引き継がれます。
          </>
        ) : (
          <>
            お支払い情報をご登録いただいても、無料期間は最後までお使いいただけます。<br />
            投稿・テーマ・QRコード・ご登録の端末は、そのまま引き継がれます。
          </>
        )}
        {billingStartLabel && (
          <>
            <br />
            <Box component="span" sx={{ fontWeight: 600, color: AppColors.textPrimary }}>
              料金の発生は{billingStartLabel}からです。
            </Box>
          </>
        )}
      </Typography>

      {!hasAddress && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          お支払いのお手続きには、郵便番号と住所のご登録が必要です。<br />
          上の「基本情報」の編集からご登録ください。
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
        <Box
          sx={{
            p: 1.5,
            backgroundColor: 'white',
            borderRadius: 1.5,
            border: `1px solid ${AppColors.border}`,
          }}
        >
          <Typography sx={{ fontSize: 13, color: AppColors.textSecondary }}>ご利用料金</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: AppColors.textPrimary }}>
            {PLAN_PRICE_LABEL}
          </Typography>
        </Box>

        <FormControl>
          <Typography sx={{ fontSize: 13, color: AppColors.textSecondary, mb: 0.5 }}>
            お支払い方法
          </Typography>
          <RadioGroup
            row
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as 'card' | 'bank_transfer')}
          >
            <FormControlLabel value="card" control={<Radio disabled={isLoading} />} label="クレジットカード" />
            <FormControlLabel value="bank_transfer" control={<Radio disabled={isLoading} />} label="銀行振込" />
          </RadioGroup>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !hasAddress}
          sx={{
            alignSelf: 'flex-start',
            px: 3,
            backgroundColor: AppColors.primary,
            '&:hover': { backgroundColor: AppColors.primaryDark },
            '&:disabled': { backgroundColor: AppColors.textTertiary, color: 'white' },
          }}
        >
          {isLoading ? <CircularProgress size={22} color="inherit" /> : '有料利用へ切り替える'}
        </Button>

        <Typography sx={{ fontSize: 12, color: AppColors.textSecondary }}>
          {paymentMethod === 'card'
            ? 'お支払い画面へ移動します。カード情報はStripe社の画面でご入力いただきます。'
            : 'お手続き後、請求書を発行いたします。お支払い期限は発行から14日です。'}
        </Typography>
      </Box>
    </Card>
  );
};

/** 課金開始日の案内。無料期間の終了日の翌日にあたる */
function formatBillingStart(value: { toDate?: () => Date } | Date | null | undefined): string | null {
  if (!value) return null;
  const end = value instanceof Date ? value : value.toDate?.();
  if (!end) return null;

  const start = new Date(end.getTime() + 1);
  const now = new Date();

  // 終了日を過ぎている場合は、今から見た翌月1日になる
  if (start.getTime() <= now.getTime()) {
    const JST = 9 * 60 * 60 * 1000;
    const jst = new Date(now.getTime() + JST);
    const next = new Date(Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth() + 1, 1) - JST);
    return next.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  return start.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
}
